import React from 'react';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';

import {
  COMP_TYPE,
  PLAN_IDS,
  PLANS,
  GLOBAL_PLAN_ORDER,
  DURATIONS,
  DURATION_BY_ID,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LIST,
  generateLicenseKeyHex,
  generateCompanyId,
  computeTrialDates,
  computeRenewalExpiry,
  formatDate,
  computeAuthorities,
  computePricing,
  getAvailableAddOns,
  hasLinkAccounts,
  ADD_ON_BY_ID,
} from './wizardConfig';

import './CompanyWizardV3.css';

const STEPS = [
  { num: 1, label: 'Company Info' },
  { num: 2, label: 'Plans & Add-Ons' },
  { num: 3, label: 'License & Review' },
  { num: 4, label: 'Payment Record' },
];

const NDS_STEPS = [
  { num: 1, label: 'Company Info' },
  { num: 2, label: 'Linked Locations' },
  { num: 3, label: 'Status & Review' },
];

const NDS_COMPANY_NAME = 'NuVe System';

// Backend stores license duration as '1M' / '3M' / '6M' / '1Y' (legacy)
// or '1' / '3' / '6' / '12' (v3). The wizard's DURATION_BY_ID is keyed on
// the v3 strings — normalize before any lookup or `selDur` write.
function normalizeDuration(raw) {
  if (raw == null) return '1';
  const str = String(raw).trim();
  if (DURATION_BY_ID[str]) return str;
  const legacyMap = {
    '1M': '1',
    '3M': '3',
    '6M': '6',
    '1Y': '12',
    '12M': '12',
  };
  if (legacyMap[str.toUpperCase()]) return legacyMap[str.toUpperCase()];
  const digits = str.replace(/[^0-9]/g, '');
  if (digits && DURATION_BY_ID[digits]) return digits;
  return '1';
}

// Best-effort match: existing record's planName -> wizard plan id.
function inferPlanId(planName) {
  if (!planName) return PLAN_IDS.PREMIUM;
  const lower = String(planName).toLowerCase();
  if (lower.includes('alpha')) return PLAN_IDS.ALPHA;
  if (lower.includes('elite') || lower.includes('pro'))
    return PLAN_IDS.PRO_ELITE;
  if (lower.includes('standard') || lower.includes('basic'))
    return PLAN_IDS.STANDARD;
  return PLAN_IDS.PREMIUM;
}

class CompanyWizardV3 extends React.Component {
  constructor(props) {
    super(props);

    const isEdit = props.mode === 'edit';
    const ec = props.editingCompany || {};

    // Trial eligibility: brand-new companies (create flow) always get
    // the trial. Existing companies normally don't — except when their
    // `trialUsed` flag is false. The Reset License flow on the backend
    // resets `trialUsed` to false when the revoked license was a free
    // trial (amount = "0"), so a brand-new company that fixes a wrong
    // plan / add-on choice mid-trial keeps its trial eligibility.
    const trialEligible = !isEdit || ec.trialUsed === false;

    const startDt = new Date();
    const initialDur =
      isEdit && ec.licenseDuration
        ? normalizeDuration(ec.licenseDuration)
        : '1';
    const dates = computeTrialDates(
      startDt,
      (DURATION_BY_ID[initialDur] || DURATIONS[0]).months
    );

    // Prefer the explicit companyType stored on the document. Fall back to
    // inferring from planName for legacy rows that pre-date the field.
    const storedCompType = ec.companyType || ec.compType;
    const initialCompType = isEdit
      ? storedCompType === COMP_TYPE.ALPHA
        ? COMP_TYPE.ALPHA
        : storedCompType === COMP_TYPE.GLOBAL
        ? COMP_TYPE.GLOBAL
        : inferPlanId(ec.planName) === PLAN_IDS.ALPHA
        ? COMP_TYPE.ALPHA
        : COMP_TYPE.GLOBAL
      : COMP_TYPE.GLOBAL;
    const initialPlan = isEdit
      ? initialCompType === COMP_TYPE.ALPHA
        ? PLAN_IDS.ALPHA
        : inferPlanId(ec.planName)
      : PLAN_IDS.PREMIUM;
    const initialAddOns = isEdit && Array.isArray(ec.addOns) ? ec.addOns : [];

    this.state = {
      step: 1,
      anim: '',
      trialEligible,
      // Snapshot of the duration/amount we loaded from the existing license.
      // Used in edit mode to detect a renewal request on save.
      origDur: isEdit && ec.licenseDuration ? String(ec.licenseDuration) : null,
      origAmount: null,

      // Step 1
      compType: initialCompType,
      coName: ec.name || '',
      contact: ec.contactName || '',
      phone: ec.phone || '',
      addr1: ec.addressLine1 || '',
      addr2: ec.addressLine2 || '',
      city: ec.city || '',
      st: ec.state || '',
      zip: ec.postalCode || '',
      country: ec.country || 'US',
      addressOpen: true,
      linkedCompanies: ec.linkedCompanies || [],
      linkedSearch: '',

      // Step 2
      selPlan: initialPlan,
      selAdd: initialAddOns,
      selDur: initialDur,

      // Step 3
      licKey: (isEdit && (ec.licenseKey || ec.key)) || generateLicenseKeyHex(),
      coId: (isEdit && (ec.coId || ec.companyId)) || generateCompanyId(),
      companyCode: isEdit ? ec.companyCode || '' : '',
      startDt: (isEdit && ec.startDate) || dates.startDate.toISOString(),
      trialEnd: (isEdit && ec.trialEnd) || dates.trialEnd.toISOString(),
      trialDays: (isEdit && ec.trialDays) || dates.trialDays,
      expDt:
        (isEdit && (ec.expiryDate || ec.licenseExpiryDate)) ||
        dates.expiryDate.toISOString(),
      copied: false,
      // New companies are always created on hold — the backend hardcodes
      // status: "hold" / enabled: false in CompanyService.create regardless
      // of what the wizard sends, so default the UI picker to match.
      enableCompany:
        isEdit && typeof ec.enabled === 'boolean' ? ec.enabled : false,
      // Tri-state company status: active | hold | closed
      companyStatus: isEdit
        ? ec.status && ['active', 'hold', 'closed'].includes(ec.status)
          ? ec.status
          : typeof ec.enabled === 'boolean' && !ec.enabled
          ? 'hold'
          : 'active'
        : 'hold',

      // Step 4
      payM: ec.paymentMethod || PAYMENT_METHODS.CASH_ZELLE,
      payDetails: ec.paymentDetails || {},
      confirmed: false,
      isSaving: false,

      // "Reset License" / "Fix Mistakes" confirm dialog
      resetLicenseDialogOpen: false,

      // Per-location NFC Ticket-Out toggle. Defaults on so existing
      // companies keep today's behavior; Alpha locations that don't use
      // NFC can uncheck to hide the NFC-driven machine auto-fill.
      nfcTicketOutEnabled:
        isEdit && typeof ec.nfcTicketOutEnabled === 'boolean'
          ? ec.nfcTicketOutEnabled
          : true,
    };
  }

  // Revoke the active license so the wizard unlocks the plan / add-on /
  // duration controls. Used when the admin originally chose the wrong
  // plan or add-ons and needs to redo step 2.
  handleResetLicense = () => {
    if (!this.props.revokeLicenseKey || !this.props.editingCompany?.companyId) {
      this.setState({ resetLicenseDialogOpen: false });
      return;
    }
    this.props.revokeLicenseKey({
      companyId: this.props.editingCompany.companyId,
      isActivated: false,
    });
    if (this.props.listCompanies) {
      this.props.listCompanies();
    }
    this.setState({ resetLicenseDialogOpen: false });
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  componentDidMount() {
    if (
      this.props.mode === 'edit' &&
      this.props.getLicenseKey &&
      this.props.editingCompany?.companyId
    ) {
      this.props.getLicenseKey({
        companyId: this.props.editingCompany.companyId,
      });
    }
  }

  componentWillUnmount() {
    if (this._savingTimeout) clearTimeout(this._savingTimeout);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.mode === 'edit' &&
      this.props.licenseKey !== prevProps.licenseKey &&
      this.props.licenseKey?.key
    ) {
      const lk = this.props.licenseKey;
      // Normalize so legacy '3M' / '1Y' values map to the wizard's
      // DURATION_BY_ID keys ('3' / '12'). Without this the picker can't
      // resolve the loaded duration and step 3 silently resets to default.
      const newDur = lk.duration
        ? normalizeDuration(lk.duration)
        : this.state.selDur;
      const newAmount = lk.amount != null ? String(lk.amount) : null;
      this.setState({
        licKey: lk.key,
        startDt: lk.startDate || this.state.startDt,
        expDt: lk.expiryDate || this.state.expDt,
        selDur: newDur,
        origDur: newDur,
        origAmount: newAmount,
      });
    }
  }

  // ---- Date refresh whenever duration or step changes ---------------------
  // The displayed expiry must match whatever the backend will actually store
  // for this save, which differs by case:
  //   1. Trial-eligible (new company, or a reset trial): the trial formula
  //      (free until the next 15th, then `months` paid → trialEnd + duration).
  //      createLicenseKeyForNewCompany persists exactly this value.
  //   2. Edit with an ACTIVE license: no renewal is issued on save
  //      (isLicenseChanged() === false), so the active license keeps its
  //      stored expiry. Preserve the loaded value — do NOT recompute.
  //   3. Renewal (edit, no active license): save triggers renewLicenseKey,
  //      which sets startDate=now and computes expiry via
  //      getNextCycleEndDate(now, months). Mirror that with computeRenewalExpiry.
  // Previously every case used the trial formula, so cases 2/3 showed a date
  // one cycle too late (e.g. Aug 15 when the real expiry — and the profile —
  // was Jul 15).
  refreshDates = (durationId) => {
    const months = (DURATION_BY_ID[durationId] || DURATIONS[0]).months;

    if (this.state.trialEligible) {
      const dates = computeTrialDates(new Date(), months);
      this.setState({
        startDt: dates.startDate.toISOString(),
        trialEnd: dates.trialEnd.toISOString(),
        trialDays: dates.trialDays,
        expDt: dates.expiryDate.toISOString(),
      });
      return;
    }

    const ec = this.props.editingCompany || {};
    const storedExpiry = ec.expiryDate || ec.licenseExpiryDate;
    if (this.hasActiveLicense() && storedExpiry) {
      this.setState({
        startDt: ec.startDate || this.state.startDt,
        expDt: storedExpiry,
      });
      return;
    }

    const expiry = computeRenewalExpiry(new Date(), months);
    this.setState({
      startDt: new Date().toISOString(),
      expDt: expiry.toISOString(),
    });
  };

  // ---- Navigation ---------------------------------------------------------
  goTo = (next) => {
    if (next === this.state.step || this.state.anim) return;
    const fwd = next > this.state.step;
    this.setState({ anim: fwd ? 'fwd' : 'back' });
    setTimeout(() => {
      this.setState({ step: next, anim: '' });
      if (next === 3) this.refreshDates(this.state.selDur);
    }, 200);
  };

  // ---- Field handlers -----------------------------------------------------
  setField = (name) => (e) => {
    this.setState({ [name]: e.target.value });
  };

  setCompType = (compType) => {
    if (compType === COMP_TYPE.ALPHA) {
      this.setState({
        compType,
        selPlan: PLAN_IDS.ALPHA,
        selAdd: [],
        selDur: '1',
      });
    } else {
      this.setState({
        compType,
        selPlan: PLAN_IDS.PREMIUM,
      });
    }
  };

  toggleAddOn = (id) => {
    this.setState((prev) => {
      const set = new Set(prev.selAdd);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { selAdd: Array.from(set) };
    });
  };

  pickPlan = (id) => this.setState({ selPlan: id });

  pickDuration = (id) => {
    this.setState({ selDur: id });
    this.refreshDates(id);
  };

  pickPayment = (id) => this.setState({ payM: id, payDetails: {} });

  setPayDetail = (key) => (e) => {
    // React 16 pools synthetic events, so by the time the functional
    // setState updater runs the event has been nullified and
    // `e.target.value` throws "Cannot read properties of null". Capture
    // the value synchronously before scheduling the state update.
    const value = e.target.value;
    this.setState((prev) => ({
      payDetails: { ...prev.payDetails, [key]: value },
    }));
  };

  copyLicense = () => {
    copy(this.state.licKey);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  isNDS() {
    const ec = this.props.editingCompany || {};
    return this.props.mode === 'edit' && ec.name === NDS_COMPANY_NAME;
  }

  // ---- Activate / save ----------------------------------------------------
  handleActivate = () => {
    // Guard against double-submission: once a save is in flight the
    // button disables itself, but a rapid double-click or keyboard
    // Enter can still fire the handler twice before React re-renders.
    // Returning here prevents a second /company/create or /company/update
    // call which was producing duplicate companies.
    if (this.state.isSaving || this.state.confirmed) return;
    const isEdit = this.props.mode === 'edit';
    const ec = this.props.editingCompany || {};
    this.setState({ isSaving: true });
    // Safety reset: the parent only fires the success callback, so a
    // failed save (network drop, validation error) would otherwise leave
    // the button disabled forever. Re-enable after 30s so the admin can
    // retry without reloading.
    this._savingTimeout = setTimeout(() => {
      this.setState((s) =>
        s.isSaving && !s.confirmed ? { isSaving: false } : null
      );
    }, 30000);

    // NDS is the parent company — no plan/license/payment needed.
    // Send all authorities and only the identity + linked locations.
    if (this.isNDS()) {
      const payload = {
        companyId: ec.companyId,
        name: this.state.coName,
        contactName: this.state.contact,
        phone: this.state.phone,
        addressLine1: this.state.addr1,
        addressLine2: this.state.addr2,
        city: this.state.city,
        state: this.state.st,
        postalCode: this.state.zip,
        country: this.state.country,
        linkedCompanies: this.state.linkedCompanies,
        // Authorities are ignored by the backend for NDS (it always
        // grants all roles), but send an empty array so the field
        // exists — the backend will override with all roles.
        authorities: [],
        // Preserve existing plan/license fields as-is
        planName: ec.planName,
        planId: ec.planId,
        compType: ec.companyType || ec.compType,
        addOns: ec.addOns || [],
        licenseDuration: ec.licenseDuration,
        // NDS is always active
        enabled: true,
        status: 'active',
        nfcTicketOutEnabled: this.state.nfcTicketOutEnabled,
      };
      this.props.onSave(payload, () =>
        this.setState({ confirmed: true, isSaving: false })
      );
      return;
    }

    const pricing = computePricing({
      planId: this.state.selPlan,
      addOnIds: this.state.selAdd,
      durationId: this.state.selDur,
    });
    let authorities = computeAuthorities(this.state.selPlan, this.state.selAdd);
    // When NFC Ticket-Out is disabled for this location, strip the
    // corresponding role so the backend also drops its mapped route.
    if (!this.state.nfcTicketOutEnabled) {
      authorities = authorities.filter((r) => r !== 'ROLE_TICKET_NFC');
    } else if (!authorities.includes('ROLE_TICKET_NFC')) {
      authorities.push('ROLE_TICKET_NFC');
    }

    const payload = {
      // Required for the update path in ManageCompanyTable.handleSave
      companyId: isEdit ? ec.companyId : undefined,
      // identity
      name: this.state.coName,
      contactName: this.state.contact,
      phone: this.state.phone,
      addressLine1: this.state.addr1,
      addressLine2: this.state.addr2,
      city: this.state.city,
      state: this.state.st,
      postalCode: this.state.zip,
      country: this.state.country,
      linkedCompanies: this.state.linkedCompanies,

      // plan
      planName: PLANS[this.state.selPlan].name,
      planId: this.state.selPlan,
      compType: this.state.compType,
      addOns: this.state.selAdd,
      licenseDuration: this.state.selDur,
      amount: pricing.total,
      monthlyRate: pricing.monthly,
      grossTotal: pricing.gross,
      durationDiscount: pricing.discount,
      authorities,

      // license / dates
      licenseKey: this.state.licKey,
      coId: this.state.coId,
      startDate: this.state.startDt,
      trialEnd: this.state.trialEnd,
      trialDays: this.state.trialDays,
      expiryDate: this.state.expDt,
      enableCompany: this.state.companyStatus === 'active',
      // CompanyService.update() reads `enabled` and `status`; mirror the
      // tri-state picker so changes in edit mode actually persist. Only an
      // explicit Active selection enables the company; Hold and Closed both
      // disable login (status differs for reporting / dashboards).
      enabled: this.state.companyStatus === 'active',
      status: this.state.companyStatus,

      // payment record (internal only)
      paymentMethod: this.state.payM,
      paymentDetails: this.state.payDetails,

      nfcTicketOutEnabled: this.state.nfcTicketOutEnabled,
    };

    // Only show the activation/saved confirmation after the server actually
    // accepts the create/update. Otherwise a failed save (network or server
    // validation error) would still flash "Company activated — payment
    // pending" with the company code/total, misleading the user.
    this.props.onSave(payload, () =>
      this.setState({ confirmed: true, isSaving: false })
    );

    // Edit-mode renewal: if duration/amount changed (or there is no active
    // license), kick off the paid renewal so a new licensekey row is created
    // server-side. Trial dates are intentionally omitted — renewLicenseKey
    // sets startDate=now and computes expiry from `duration`.
    if (
      isEdit &&
      this.props.handleRenewLicenseKey &&
      ec.companyId &&
      this.isLicenseChanged()
    ) {
      this.props.handleRenewLicenseKey(
        {
          companyId: ec.companyId,
          duration: this.state.selDur,
          amount: String(pricing.total),
          // Phase 1: forward the Step 4 payment fields so the backend
          // can log a licensePayment row alongside the new licensekey
          // row. These are ignored by Phase 2 Stripe once that lands.
          paymentMethod: this.state.payM,
          paymentDetails: this.state.payDetails,
          planName: PLANS[this.state.selPlan].name,
        },
        () => {}
      );
    }
  };

  // ---- Renderers ----------------------------------------------------------
  renderTopBar() {
    return (
      <div className="cw3-topbar">
        <div className="cw3-logo">
          <div className="cw3-logo__shield">N</div>
          NUVE SYSTEM
        </div>
        <span className="cw3-admin-badge">ADMIN</span>
      </div>
    );
  }

  renderStepper() {
    const { step } = this.state;
    const clickable = this.props.mode === 'edit';
    const steps = this.isNDS() ? NDS_STEPS : STEPS;
    return (
      <div className="cw3-stepper">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div
              className={`cw3-step ${
                step === s.num ? 'cw3-step--active' : ''
              } ${step > s.num ? 'cw3-step--done' : ''}`}
              style={{ cursor: clickable ? 'pointer' : 'default' }}
              onClick={clickable ? () => this.goTo(s.num) : undefined}
            >
              <div className="cw3-step__circle">
                {step > s.num ? '\u2713' : s.num}
              </div>
              <div className="cw3-step__label">{s.label}</div>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`cw3-step__line ${
                  step > s.num ? 'cw3-step__line--done' : ''
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // ---- Step 1 -------------------------------------------------------------
  renderStep1() {
    const isAlpha = this.state.compType === COMP_TYPE.ALPHA;
    const nds = this.isNDS();
    return (
      <div>
        {!nds && (
          <div className="cw3-comp-row">
            <div
              className={`cw3-comp-card cw3-comp-card--global ${
                !isAlpha ? 'cw3-comp-card--active' : ''
              }`}
              onClick={() => this.setCompType(COMP_TYPE.GLOBAL)}
            >
              <div className="cw3-comp-card__name">Global Company</div>
            </div>
            <div
              className={`cw3-comp-card cw3-comp-card--alpha ${
                isAlpha ? 'cw3-comp-card--active' : ''
              }`}
              onClick={() => this.setCompType(COMP_TYPE.ALPHA)}
            >
              <div className="cw3-comp-card__name">Alpha Locations</div>
            </div>
          </div>
        )}

        <label className="cw3-label">Business Name *</label>
        <input
          className="cw3-input"
          value={this.state.coName}
          onChange={this.setField('coName')}
        />

        <label className="cw3-label">Contact Name *</label>
        <input
          className="cw3-input"
          value={this.state.contact}
          onChange={this.setField('contact')}
        />

        <label className="cw3-label">Phone *</label>
        <div className="cw3-input-wrap">
          <span className="cw3-input-wrap__prefix">+1</span>
          <input
            className="cw3-input cw3-input--phone"
            value={this.state.phone}
            onChange={this.setField('phone')}
          />
        </div>

        {/* Address collapsible */}
        <div className="cw3-collapse">
          <div
            className="cw3-collapse__header"
            onClick={() =>
              this.setState({ addressOpen: !this.state.addressOpen })
            }
          >
            <span>Address Information</span>
            <span
              className={`cw3-collapse__chev ${
                this.state.addressOpen ? 'cw3-collapse__chev--open' : ''
              }`}
            >
              ▾
            </span>
          </div>
          {this.state.addressOpen && (
            <div className="cw3-collapse__body">
              <input
                className="cw3-input"
                placeholder="Address Line 1"
                value={this.state.addr1}
                onChange={this.setField('addr1')}
              />
              <div style={{ height: 8 }} />
              <input
                className="cw3-input"
                placeholder="Address Line 2"
                value={this.state.addr2}
                onChange={this.setField('addr2')}
              />
              <div style={{ height: 8 }} />
              <div className="cw3-row">
                <input
                  className="cw3-input"
                  placeholder="City *"
                  value={this.state.city}
                  onChange={this.setField('city')}
                />
                <input
                  className="cw3-input"
                  placeholder="State"
                  value={this.state.st}
                  onChange={this.setField('st')}
                />
              </div>
              <div style={{ height: 8 }} />
              <div className="cw3-row">
                <input
                  className="cw3-input"
                  placeholder="Zip"
                  value={this.state.zip}
                  onChange={this.setField('zip')}
                />
                <select
                  className="cw3-input"
                  value={this.state.country}
                  onChange={this.setField('country')}
                >
                  <option value="US">United States</option>
                  <option value="MX">Mexico</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="cw3-actions">
          <button className="cw3-btn-ghost" onClick={this.props.onClose}>
            Cancel
          </button>
          <button
            className="cw3-btn-gold"
            onClick={() => this.goTo(2)}
            disabled={
              !this.state.coName || !this.state.contact || !this.state.phone
            }
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  // ---- Step 2 -------------------------------------------------------------
  renderPlanCards() {
    const isAlpha = this.state.compType === COMP_TYPE.ALPHA;
    const planIds = isAlpha ? [PLAN_IDS.ALPHA] : GLOBAL_PLAN_ORDER;
    return (
      <div
        className="cw3-plans"
        style={isAlpha ? { gridTemplateColumns: '1fr' } : undefined}
      >
        {planIds.map((id) => {
          const plan = PLANS[id];
          const active = this.state.selPlan === id;
          return (
            <div
              key={id}
              className={`cw3-plan ${active ? 'cw3-plan--active' : ''}`}
              style={{
                borderColor: active ? plan.color : '#e2e6f0',
                color: plan.color,
                boxShadow: active ? `0 8px 24px ${plan.color}33` : 'none',
              }}
              onClick={() => this.pickPlan(id)}
            >
              {plan.badge && (
                <span className="cw3-plan__badge">{plan.badge}</span>
              )}
              <div
                className={`cw3-plan__radio ${
                  active ? 'cw3-plan__radio--checked' : ''
                }`}
              />
              <div className="cw3-plan__name">{plan.name.toUpperCase()}</div>
              <div className="cw3-plan__price">${plan.price}</div>
              <div className="cw3-plan__per">/mo</div>
            </div>
          );
        })}
      </div>
    );
  }

  renderPlanDetail() {
    const plan = PLANS[this.state.selPlan];
    if (!plan) return null;
    return (
      <div className="cw3-plan-detail">
        {plan.sections.map((section) => (
          <div key={section.title} className="cw3-plan-detail__section">
            <span
              className="cw3-section-title"
              style={{ background: section.color }}
            >
              {section.title}
            </span>
            {section.note ? (
              <div className="cw3-feature-note">{section.note}</div>
            ) : (
              <ul className="cw3-feature-grid">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }

  renderAddOns() {
    if (this.state.compType === COMP_TYPE.ALPHA) return null;
    const available = getAvailableAddOns(this.state.selPlan);
    if (available.length === 0) return null;
    return (
      <>
        <label className="cw3-label">Add-Ons</label>
        {available.map((a) => {
          const active = this.state.selAdd.includes(a.id);
          return (
            <div
              key={a.id}
              className={`cw3-addon ${active ? 'cw3-addon--active' : ''}`}
              onClick={() => this.toggleAddOn(a.id)}
            >
              <span
                className={`cw3-check ${active ? 'cw3-check--checked' : ''}`}
              />
              <div className="cw3-addon__body">
                <span className="cw3-addon__name">{a.name}</span>
                <span className="cw3-addon__price">+${a.price}/mo</span>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  addLinkedCompany = (id) => {
    this.setState((prev) => {
      if (prev.linkedCompanies.includes(id)) return null;
      return {
        linkedCompanies: [...prev.linkedCompanies, id],
        linkedSearch: '',
      };
    });
  };

  removeLinkedCompany = (id) => {
    this.setState((prev) => ({
      linkedCompanies: prev.linkedCompanies.filter((x) => x !== id),
    }));
  };

  // NDS always shows linked locations — no plan gate check needed
  renderLinkedLocationsNDS() {
    const editingId =
      (this.props.editingCompany && this.props.editingCompany.companyId) ||
      null;
    const linkedSet = new Set(this.state.linkedCompanies);
    const allCompanies = this.props.companies || [];
    const term = this.state.linkedSearch.trim().toLowerCase();

    const lookup = allCompanies.reduce((acc, c) => {
      acc[c.companyId] = c;
      return acc;
    }, {});

    const results = term
      ? allCompanies
          .filter(
            (c) => c.companyId !== editingId && !linkedSet.has(c.companyId)
          )
          .filter(
            (c) =>
              (c.name || '').toLowerCase().includes(term) ||
              String(c.companyCode || '')
                .toLowerCase()
                .includes(term)
          )
          .slice(0, 8)
      : [];

    return (
      <div className="cw3-linked">
        <label className="cw3-label">Linked Locations</label>
        <div className="cw3-linked__hint">
          All companies managed by NuVe System. Owner accounts automatically
          receive access to every linked location.
        </div>

        {this.state.linkedCompanies.length > 0 && (
          <div className="cw3-chips">
            {this.state.linkedCompanies.map((id) => {
              const co = lookup[id];
              const label = co
                ? co.companyCode
                  ? `${co.name} (${co.companyCode})`
                  : co.name
                : id;
              return (
                <span key={id} className="cw3-chip">
                  <span className="cw3-chip__dot" />
                  {label}
                  <button
                    type="button"
                    className="cw3-chip__x"
                    onClick={() => this.removeLinkedCompany(id)}
                    aria-label={`Remove ${label}`}
                  >
                    &times;
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="cw3-linked-search">
          <input
            className="cw3-input"
            placeholder="Search locations by name or company code..."
            value={this.state.linkedSearch}
            onChange={(e) => this.setState({ linkedSearch: e.target.value })}
          />
          {term && (
            <div className="cw3-linked-results">
              {results.length === 0 ? (
                <div className="cw3-linked-empty">No matching locations</div>
              ) : (
                results.map((c) => (
                  <div
                    key={c.companyId}
                    className="cw3-linked-result"
                    onClick={() => this.addLinkedCompany(c.companyId)}
                  >
                    <div className="cw3-linked-result__name">{c.name}</div>
                    <div className="cw3-linked-result__id">
                      {c.companyCode || ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderNfcTicketOutToggle() {
    const on = this.state.nfcTicketOutEnabled;
    return (
      <label className={`cw3-toggle-card ${on ? 'cw3-toggle-card--on' : ''}`}>
        <span className="cw3-checkbox">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) =>
              this.setState({ nfcTicketOutEnabled: e.target.checked })
            }
          />
          <span className="cw3-checkbox__box" aria-hidden="true">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 8.5 6.5 12 13 4.5" />
            </svg>
          </span>
        </span>
        <span className="cw3-toggle-text">
          <span className="cw3-toggle-label">
            Enable NFC Ticket-Out for this location
          </span>
          <span className="cw3-toggle-hint">
            Uncheck for locations that don&apos;t use NFC ticket-out. The
            Machine field on the Ticket Out screen will stay manually editable.
          </span>
        </span>
      </label>
    );
  }

  renderLinkedLocations() {
    if (!hasLinkAccounts(this.state.selPlan, this.state.selAdd)) return null;

    const editingId =
      (this.props.editingCompany && this.props.editingCompany.companyId) ||
      null;
    const linkedSet = new Set(this.state.linkedCompanies);
    const allCompanies = this.props.companies || [];
    const term = this.state.linkedSearch.trim().toLowerCase();

    const lookup = allCompanies.reduce((acc, c) => {
      acc[c.companyId] = c;
      return acc;
    }, {});

    const results = term
      ? allCompanies
          .filter(
            (c) => c.companyId !== editingId && !linkedSet.has(c.companyId)
          )
          .filter(
            (c) =>
              (c.name || '').toLowerCase().includes(term) ||
              String(c.companyCode || '')
                .toLowerCase()
                .includes(term)
          )
          .slice(0, 8)
      : [];

    return (
      <div className="cw3-linked">
        <label className="cw3-label">Linked Locations</label>
        <div className="cw3-linked__hint">
          Link this company to other locations so they can share data and be
          managed together.
        </div>

        {this.state.linkedCompanies.length > 0 && (
          <div className="cw3-chips">
            {this.state.linkedCompanies.map((id) => {
              const co = lookup[id];
              const label = co
                ? co.companyCode
                  ? `${co.name} (${co.companyCode})`
                  : co.name
                : id;
              return (
                <span key={id} className="cw3-chip">
                  <span className="cw3-chip__dot" />
                  {label}
                  <button
                    type="button"
                    className="cw3-chip__x"
                    onClick={() => this.removeLinkedCompany(id)}
                    aria-label={`Remove ${label}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="cw3-linked-search">
          <input
            className="cw3-input"
            placeholder="Search locations by name or company code…"
            value={this.state.linkedSearch}
            onChange={(e) => this.setState({ linkedSearch: e.target.value })}
          />
          {term && (
            <div className="cw3-linked-results">
              {results.length === 0 ? (
                <div className="cw3-linked-empty">No matching locations</div>
              ) : (
                results.map((c) => (
                  <div
                    key={c.companyId}
                    className="cw3-linked-result"
                    onClick={() => this.addLinkedCompany(c.companyId)}
                  >
                    <div className="cw3-linked-result__name">{c.name}</div>
                    <div className="cw3-linked-result__id">
                      {c.companyCode || ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderDurations() {
    if (this.state.compType === COMP_TYPE.ALPHA) return null;
    const locked = this.hasActiveLicense();
    return (
      <>
        <label className="cw3-label">License Duration</label>
        {locked && (
          <div
            style={{
              fontSize: 12,
              color: '#b45309',
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: 6,
              padding: '8px 10px',
              marginBottom: 8,
            }}
          >
            Active license — duration is locked. You can renew with a new
            duration after the current license expires.
          </div>
        )}
        <div
          className="cw3-duration-grid"
          style={locked ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          {DURATIONS.map((d) => {
            const active = this.state.selDur === d.id;
            return (
              <div
                key={d.id}
                className={`cw3-duration ${
                  active ? 'cw3-duration--active' : ''
                }`}
                onClick={() => !locked && this.pickDuration(d.id)}
              >
                <div className="cw3-duration__label">{d.label}</div>
                {d.badge && (
                  <span
                    className="cw3-duration__badge"
                    style={{ background: d.badgeColor }}
                  >
                    {d.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  renderSubtotal() {
    const pricing = computePricing({
      planId: this.state.selPlan,
      addOnIds: this.state.selAdd,
      durationId: this.state.selDur,
    });
    const plan = PLANS[this.state.selPlan] || PLANS[PLAN_IDS.PREMIUM];
    const duration = DURATION_BY_ID[this.state.selDur] || DURATIONS[0];
    return (
      <div className="cw3-subtotal">
        <div className="cw3-subtotal__row">
          <span>{plan.name} Plan</span>
          <span>${plan.price}/mo</span>
        </div>
        {this.state.selAdd.map((id) => {
          const a = ADD_ON_BY_ID[id];
          if (!a) return null;
          return (
            <div key={id} className="cw3-subtotal__row">
              <span>{a.name}</span>
              <span>+${a.price}/mo</span>
            </div>
          );
        })}
        <div className="cw3-subtotal__row">
          <span>{duration.label}</span>
          <span>x {duration.months}</span>
        </div>
        {pricing.discount > 0 && (
          <div className="cw3-subtotal__row cw3-subtotal__row--green">
            <span>
              Duration discount ({Math.round(pricing.discountPct * 100)}%)
            </span>
            <span>-${pricing.discount}</span>
          </div>
        )}
        <hr className="cw3-subtotal__divider" />
        {this.state.trialEligible && (
          <div className="cw3-trial-note">
            1st Month FREE — no charge until trial ends
          </div>
        )}
        <div className="cw3-subtotal__total">
          <span>
            {this.state.trialEligible ? 'Total After Trial' : 'Total Due'}
          </span>
          <span>
            {pricing.discount > 0 && <small>${pricing.gross}</small>}$
            {pricing.total}
          </span>
        </div>
      </div>
    );
  }

  renderStep2() {
    // NDS: only show linked locations — no plan/add-ons/pricing
    if (this.isNDS()) {
      return (
        <div>
          <div
            className="cw3-trial-banner"
            style={{ background: '#e0f2fe', borderColor: '#7dd3fc' }}
          >
            <div className="cw3-trial-banner__num">N</div>
            <div className="cw3-trial-banner__text">
              NuVe System — parent company. All roles and permissions are
              granted automatically. No plan or license required.
            </div>
          </div>

          {this.renderLinkedLocationsNDS()}

          <div className="cw3-actions">
            <button className="cw3-btn-ghost" onClick={() => this.goTo(1)}>
              BACK
            </button>
            <button className="cw3-btn-gold" onClick={() => this.goTo(3)}>
              CONTINUE
            </button>
          </div>
        </div>
      );
    }

    const isAlpha = this.state.compType === COMP_TYPE.ALPHA;
    const locked = this.hasActiveLicense();
    return (
      <div style={locked ? { '--cw3-locked': 1 } : undefined}>
        {locked ? (
          <div
            className="cw3-trial-banner"
            style={{
              background: '#fef3c7',
              borderColor: '#fcd34d',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div className="cw3-trial-banner__num">!</div>
            <div className="cw3-trial-banner__text" style={{ flex: 1 }}>
              Active license — plan, add-ons and duration are read-only. Profile
              changes still save.
            </div>
            <button
              type="button"
              onClick={() => this.setState({ resetLicenseDialogOpen: true })}
              style={{
                background: '#b91c1c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Reset License
            </button>
          </div>
        ) : this.state.trialEligible ? (
          <div className="cw3-trial-banner">
            <div className="cw3-trial-banner__num">1</div>
            <div className="cw3-trial-banner__text">
              First Month FREE — Pro Elite + All Add-Ons included during trial
            </div>
          </div>
        ) : (
          <div className="cw3-trial-banner">
            <div className="cw3-trial-banner__num">$</div>
            <div className="cw3-trial-banner__text">
              Existing company — paid renewal. No free trial.
            </div>
          </div>
        )}

        {isAlpha && (
          <div className="cw3-alpha-info">
            Alpha Locations: Month-to-month billing at $300/mo. All features and
            add-ons included.
          </div>
        )}

        <div
          style={locked ? { opacity: 0.55, pointerEvents: 'none' } : undefined}
        >
          {this.renderPlanCards()}
          {this.renderPlanDetail()}
          {this.renderAddOns()}
        </div>
        {this.renderLinkedLocations()}
        {this.renderNfcTicketOutToggle()}
        {this.renderDurations()}
        {this.renderSubtotal()}

        <div className="cw3-actions">
          <button className="cw3-btn-ghost" onClick={() => this.goTo(1)}>
            BACK
          </button>
          <button className="cw3-btn-gold" onClick={() => this.goTo(3)}>
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  // ---- Step 3 -------------------------------------------------------------
  renderStep3NDS() {
    return (
      <div>
        <div className="cw3-summary-grid">
          <div>
            <div className="cw3-sum-row__label">Company</div>
            <div className="cw3-sum-row__value">{this.state.coName || '—'}</div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Type</div>
            <div className="cw3-sum-row__value">Parent Company</div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Plan</div>
            <div className="cw3-sum-row__value">
              All Features (No license required)
            </div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Linked Locations</div>
            <div className="cw3-sum-row__value">
              {this.state.linkedCompanies.length} locations
            </div>
          </div>
        </div>

        {this.renderConfirmation()}

        <div className="cw3-actions">
          <button className="cw3-btn-ghost" onClick={() => this.goTo(2)}>
            BACK
          </button>
          <button
            className={`cw3-btn-gold ${
              this.state.confirmed ? 'cw3-btn-gold--green' : ''
            }`}
            onClick={this.handleActivate}
            disabled={this.state.confirmed || this.state.isSaving}
          >
            {this.state.confirmed
              ? 'SAVED'
              : this.state.isSaving
              ? 'SAVING...'
              : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    );
  }

  renderStep3() {
    const pricing = computePricing({
      planId: this.state.selPlan,
      addOnIds: this.state.selAdd,
      durationId: this.state.selDur,
    });
    const plan = PLANS[this.state.selPlan] || PLANS[PLAN_IDS.PREMIUM];
    const isAlpha = this.state.compType === COMP_TYPE.ALPHA;
    // Defensive fallback: if selDur somehow points at an unknown
    // duration (legacy data, hand-edited record, etc.) use the first
    // entry instead of crashing on duration.label below.
    const duration = DURATION_BY_ID[this.state.selDur] || DURATIONS[0];
    const isEdit = this.props.mode === 'edit';
    // In create mode the backend assigns the company code on save, so we
    // intentionally don't display any local placeholder id here — showing one
    // would mislead the user into thinking it's the real code.
    const idValue = isEdit ? this.state.companyCode || '—' : null;

    return (
      <div>
        {isEdit && (
          <div className="cw3-coid-wrap">
            <div className="cw3-coid">COMPANY CODE • {idValue}</div>
          </div>
        )}

        <div className="cw3-summary-grid">
          {isEdit && (
            <div>
              <div className="cw3-sum-row__label">Company Code</div>
              <div className="cw3-sum-row__value">{idValue}</div>
            </div>
          )}
          <div>
            <div className="cw3-sum-row__label">Company</div>
            <div className="cw3-sum-row__value">{this.state.coName || '—'}</div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Type</div>
            <div className="cw3-sum-row__value">
              {isAlpha ? 'Alpha Locations' : 'Global Company'}
            </div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Plan</div>
            <div className="cw3-sum-row__value">{plan.name}</div>
          </div>
          <div>
            <div className="cw3-sum-row__label">Duration</div>
            <div className="cw3-sum-row__value">
              {isAlpha ? 'Month-to-month' : duration.label}
            </div>
          </div>
          {this.state.trialEligible ? (
            <>
              <div>
                <div className="cw3-sum-row__label">Free Trial</div>
                <div className="cw3-sum-row__value">
                  {this.state.trialDays} days free
                </div>
              </div>
              <div>
                <div className="cw3-sum-row__label">1st Charge Due</div>
                <div className="cw3-sum-row__value">
                  Within 3 days of activation
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="cw3-sum-row__label">Start Date</div>
                <div className="cw3-sum-row__value">
                  {formatDate(this.state.startDt)}
                </div>
              </div>
              <div>
                <div className="cw3-sum-row__label">Expiry Date</div>
                <div className="cw3-sum-row__value">
                  {formatDate(this.state.expDt)}
                </div>
              </div>
            </>
          )}
        </div>

        {this.state.trialEligible ? (
          <div className="cw3-due-today">
            <div className="cw3-due-today__free">FREE</div>
            <div className="cw3-due-today__then">
              Then ${pricing.total} after trial
            </div>
          </div>
        ) : (
          <div className="cw3-due-today">
            <div className="cw3-due-today__free">${pricing.total}</div>
            <div className="cw3-due-today__then">Due now — paid renewal</div>
          </div>
        )}

        <div className="cw3-key-card">
          <div className="cw3-key-label">License Key</div>
          <div className="cw3-key-row">
            <div className="cw3-key-text">{this.state.licKey}</div>
            <button
              className={`cw3-key-copy ${
                this.state.copied ? 'cw3-key-copy--copied' : ''
              }`}
              onClick={this.copyLicense}
            >
              {this.state.copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="cw3-key-dates">
            <div>
              <div className="cw3-key-date__label">Start</div>
              <div className="cw3-key-date__value">
                {formatDate(this.state.startDt)}
              </div>
            </div>
            {this.state.trialEligible && (
              <div>
                <div className="cw3-key-date__label">Trial Ends</div>
                <div className="cw3-key-date__value cw3-key-date__value--green">
                  {formatDate(this.state.trialEnd)}
                </div>
              </div>
            )}
            <div>
              <div className="cw3-key-date__label">Expiry</div>
              <div className="cw3-key-date__value cw3-key-date__value--gray">
                {formatDate(this.state.expDt)}
              </div>
            </div>
          </div>
          {this.state.trialEligible ? (
            <div className="cw3-key-info">
              {this.state.trialDays} days free trial • First charge due within 3
              days of activation • All billing on the 15th
            </div>
          ) : (
            <div className="cw3-key-info">
              Paid renewal • Charged immediately • All billing on the 15th
            </div>
          )}
        </div>

        {/* Status picker — only shown in edit mode. New companies always
            start on hold; the master admin activates via license key. */}
        {isEdit ? (
          <div className="cw3-enable" style={{ display: 'block' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Company Status
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                {
                  id: 'active',
                  label: 'Active',
                  color: '#16a34a',
                  bg: '#e0f5e9',
                },
                { id: 'hold', label: 'Hold', color: '#b45309', bg: '#fcf4cc' },
                {
                  id: 'closed',
                  label: 'Closed',
                  color: '#dc2626',
                  bg: '#ffeee6',
                },
              ].map((opt) => {
                const active = this.state.companyStatus === opt.id;
                return (
                  <div
                    key={opt.id}
                    onClick={() =>
                      this.setState({
                        companyStatus: opt.id,
                        enableCompany: opt.id === 'active',
                      })
                    }
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `2px solid ${active ? opt.color : '#e2e6f0'}`,
                      background: active ? opt.bg : '#fff',
                      color: active ? opt.color : '#6b7280',
                      fontWeight: active ? 700 : 500,
                      fontSize: 13,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all .15s',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="cw3-enable" style={{ display: 'block' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Company Status
            </div>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                border: '2px solid #b45309',
                background: '#fcf4cc',
                color: '#b45309',
                fontWeight: 700,
                fontSize: 13,
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              Hold — Activates when the master admin enters the license key
            </div>
          </div>
        )}

        <div className="cw3-actions">
          <button className="cw3-btn-ghost" onClick={() => this.goTo(2)}>
            BACK
          </button>
          <button className="cw3-btn-gold" onClick={() => this.goTo(4)}>
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  // ---- Step 4 -------------------------------------------------------------
  renderPaymentFields() {
    const { payM, payDetails } = this.state;
    if (payM === PAYMENT_METHODS.CASH_ZELLE) {
      return (
        <div className="cw3-pay-fields">
          <input
            className="cw3-input"
            placeholder="Sender Name"
            value={payDetails.senderName || ''}
            onChange={this.setPayDetail('senderName')}
          />
          <div style={{ height: 8 }} />
          <input
            className="cw3-input"
            placeholder="Amount Sent"
            value={payDetails.amountSent || ''}
            onChange={this.setPayDetail('amountSent')}
          />
          <div style={{ height: 8 }} />
          <input
            className="cw3-input"
            placeholder="Zelle Email / Phone"
            value={payDetails.zelleHandle || ''}
            onChange={this.setPayDetail('zelleHandle')}
          />
          <div style={{ height: 8 }} />
          <input
            className="cw3-input"
            placeholder="Confirmation #"
            value={payDetails.confirmation || ''}
            onChange={this.setPayDetail('confirmation')}
          />
        </div>
      );
    }
    if (payM === PAYMENT_METHODS.CARD) {
      return (
        <div className="cw3-pay-fields">
          <input
            className="cw3-input"
            placeholder="Card Number"
            value={payDetails.cardNumber || ''}
            onChange={this.setPayDetail('cardNumber')}
          />
          <div style={{ height: 8 }} />
          <div className="cw3-row">
            <input
              className="cw3-input"
              placeholder="MM/YY"
              value={payDetails.expiry || ''}
              onChange={this.setPayDetail('expiry')}
            />
            <input
              className="cw3-input"
              placeholder="CVC"
              value={payDetails.cvc || ''}
              onChange={this.setPayDetail('cvc')}
            />
          </div>
          <div style={{ height: 8 }} />
          <input
            className="cw3-input"
            placeholder="Cardholder Name"
            value={payDetails.cardName || ''}
            onChange={this.setPayDetail('cardName')}
          />
        </div>
      );
    }
    if (payM === PAYMENT_METHODS.BANK) {
      return (
        <div className="cw3-pay-fields">
          <input
            className="cw3-input"
            placeholder="Routing Number"
            value={payDetails.routing || ''}
            onChange={this.setPayDetail('routing')}
          />
          <div style={{ height: 8 }} />
          <input
            className="cw3-input"
            placeholder="Account Number"
            value={payDetails.account || ''}
            onChange={this.setPayDetail('account')}
          />
        </div>
      );
    }
    if (payM === PAYMENT_METHODS.PENDING) {
      return (
        <div className="cw3-pay-fields">
          <input
            className="cw3-input"
            placeholder="Notes — reason or follow-up date (optional)"
            value={payDetails.notes || ''}
            onChange={this.setPayDetail('notes')}
          />
        </div>
      );
    }
    return null;
  }

  // True only when the wizard's save should also issue a renewal. Hard rule:
  // we NEVER renew while an active license exists — that would silently
  // burn the user's current license (e.g. their free trial) on a profile
  // edit. Renewal is only allowed when there is no active license at all
  // (expired, revoked, or never created), and the user explicitly came
  // through the wizard's pricing/duration step.
  isLicenseChanged() {
    if (this.props.mode !== 'edit') return false;
    if (this.props.licenseKey?.activated) return false;
    return true;
  }

  hasActiveLicense() {
    return this.props.mode === 'edit' && !!this.props.licenseKey?.activated;
  }

  activateButtonText() {
    if (this.state.confirmed) {
      return this.props.mode === 'edit' ? 'SAVED' : 'ACTIVATED';
    }
    if (this.state.isSaving) {
      return 'SAVING...';
    }
    if (this.isNDS()) {
      return 'SAVE CHANGES';
    }
    if (this.props.mode === 'edit') {
      return this.isLicenseChanged() ? 'RENEW & SAVE' : 'SAVE CHANGES';
    }
    if (this.state.payM === PAYMENT_METHODS.CASH_ZELLE)
      return 'CONFIRM (PENDING)';
    if (this.state.payM === PAYMENT_METHODS.PENDING)
      return 'ACTIVATE (NO PAYMENT)';
    return 'ACTIVATE & RECORD';
  }

  renderConfirmation() {
    if (!this.state.confirmed) return null;
    const pricing = computePricing({
      planId: this.state.selPlan,
      addOnIds: this.state.selAdd,
      durationId: this.state.selDur,
    });
    let cls = 'cw3-confirm--green';
    let msg = 'Company activated! Payment recorded.';
    if (this.state.payM === PAYMENT_METHODS.CASH_ZELLE) {
      cls = 'cw3-confirm--yellow';
      msg = 'Pending payment verification';
    } else if (this.state.payM === PAYMENT_METHODS.PENDING) {
      cls = 'cw3-confirm--purple';
      msg = 'Company activated — payment pending';
    }
    return (
      <div className={`cw3-confirm ${cls}`}>
        {msg}
        <div style={{ marginTop: 6, fontSize: 11 }}>
          {this.props.mode === 'edit'
            ? `Company Code ${this.state.companyCode || '—'} • Total $${
                pricing.total
              }`
            : `Total $${pricing.total}`}
        </div>
      </div>
    );
  }

  renderStep4() {
    const pricing = computePricing({
      planId: this.state.selPlan,
      addOnIds: this.state.selAdd,
      durationId: this.state.selDur,
    });
    const plan = PLANS[this.state.selPlan];

    return (
      <div>
        <div className="cw3-pay-header">
          <div>
            <div className="cw3-pay-header__label">Due Today</div>
            <div className="cw3-pay-header__free">
              {this.state.trialEligible ? 'FREE' : `$${pricing.total}`}
            </div>
          </div>
          <div className="cw3-pay-header__plan">
            <div className="cw3-pay-header__plan-name">{plan.name}</div>
            <div className="cw3-pay-header__plan-price">${pricing.total}</div>
          </div>
        </div>

        {this.state.trialEligible ? (
          <div className="cw3-pay-banner">
            No charge today. {this.state.trialDays} days free trial. First
            charge of ${pricing.total} due within 3 days of account activation.
          </div>
        ) : (
          <div className="cw3-pay-banner">
            Paid renewal — record payment of ${pricing.total} now. New license
            starts today and runs for{' '}
            {(DURATION_BY_ID[this.state.selDur] || DURATIONS[0]).label}.
          </div>
        )}

        {PAYMENT_METHOD_LIST.map((m) => {
          const active = this.state.payM === m.id;
          return (
            <React.Fragment key={m.id}>
              <div
                className={`cw3-pay-method ${
                  active ? 'cw3-pay-method--active' : ''
                }`}
                style={{
                  color: m.color,
                  borderColor: active ? m.color : '#e2e6f0',
                }}
                onClick={() => this.pickPayment(m.id)}
              >
                <span
                  className={`cw3-pay-method__radio ${
                    active ? 'cw3-pay-method__radio--checked' : ''
                  }`}
                />
                <div className="cw3-pay-method__body">
                  <span className="cw3-pay-method__name">{m.label}</span>
                  {m.badge && (
                    <span
                      className="cw3-pay-method__badge"
                      style={{ background: `${m.color}22`, color: m.color }}
                    >
                      {m.badge}
                    </span>
                  )}
                </div>
              </div>
              {active && this.renderPaymentFields()}
            </React.Fragment>
          );
        })}

        {this.renderConfirmation()}

        <div className="cw3-actions">
          <button className="cw3-btn-ghost" onClick={() => this.goTo(3)}>
            BACK
          </button>
          <button
            className={`cw3-btn-gold ${
              this.state.confirmed ? 'cw3-btn-gold--green' : ''
            }`}
            onClick={this.handleActivate}
            disabled={this.state.confirmed || this.state.isSaving}
          >
            {this.activateButtonText()}
          </button>
        </div>
      </div>
    );
  }

  // ---- Top-level render ---------------------------------------------------
  render() {
    const { step, anim } = this.state;
    const bodyCls = anim
      ? `cw3-card__body cw3-card__body--leaving-${
          anim === 'fwd' ? 'fwd' : 'back'
        }`
      : 'cw3-card__body';
    const nds = this.isNDS();
    const steps = nds ? NDS_STEPS : STEPS;
    const stepDef = steps[step - 1] || steps[steps.length - 1];

    return (
      <div className="cw3-bg">
        {this.renderStepper()}
        <div className="cw3-card">
          <div className="cw3-card__header">
            <div className="cw3-card__step-label">
              STEP {step} OF {steps.length}
            </div>
            <h2 className="cw3-card__title">{stepDef.label}</h2>
          </div>
          <div className={bodyCls}>
            {step === 1 && this.renderStep1()}
            {step === 2 && this.renderStep2()}
            {step === 3 && (nds ? this.renderStep3NDS() : this.renderStep3())}
            {step === 4 && !nds && this.renderStep4()}
          </div>
        </div>
        {this.renderResetLicenseDialog()}
      </div>
    );
  }

  renderResetLicenseDialog() {
    if (!this.state.resetLicenseDialogOpen) return null;
    return (
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClick={() => this.setState({ resetLicenseDialogOpen: false })}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '24px 28px',
            maxWidth: 480,
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ margin: 0, marginBottom: 8, color: '#b91c1c' }}>
            Reset License?
          </h3>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
            This will <strong>revoke the active license</strong> and disable the
            company. After confirming, reopen the company to choose the correct
            plan and add-ons, then activate a new license.
          </p>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6B7280' }}>
            Use this only when the original plan or add-ons were chosen by
            mistake. The revoked license stays in history for auditing.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => this.setState({ resetLicenseDialogOpen: false })}
              style={{
                background: '#fff',
                color: '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={this.handleResetLicense}
              style={{
                background: '#b91c1c',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 18px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Revoke & Reset
            </button>
          </div>
        </div>
      </div>
    );
  }
}

CompanyWizardV3.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  editingCompany: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  companies: PropTypes.array,
  licenseKey: PropTypes.object,
  getLicenseKey: PropTypes.func,
  handleRenewLicenseKey: PropTypes.func,
  revokeLicenseKey: PropTypes.func,
  listCompanies: PropTypes.func,
};

CompanyWizardV3.defaultProps = {
  mode: 'add',
  editingCompany: null,
  companies: [],
  licenseKey: null,
  getLicenseKey: null,
  handleRenewLicenseKey: null,
  revokeLicenseKey: null,
  listCompanies: null,
};

export default CompanyWizardV3;
